import {
  Button,
  Col,
  Divider,
  Flex,
  message,
  Modal,
  Popconfirm,
  Row,
  Spin,
  Typography,
} from 'antd'
import { Fragment, useState } from 'react'
import { useAuth } from 'ict24h-themes-react'
import {
  ProjectEntity,
  ProjectMemberEntity,
  UserEntity,
} from '../../../../../../../server-api-hrm.type'
import useCreateProjectMember from '../hooks/use-create-project-member'
import useDeleteProjectMember from '../hooks/use-delete-project-member'
import { mapMemberRole, mapMemberRoleObj } from '../constants/map.MemberRole'
import TagMember from './Tag.Member'
import useHandleSelectData from '../../../../../components/re-useable/search-select-v3/use-handle-select-data'
import { getListUserNotInProject } from '../../../../../../cau-hinh-thong-tin/modules/user/user.service'
import IconGlobalApp from '../../../../../../../utils/IconGlobalApp'
import { mapCommonIcons } from '../../../../../../../utils/mapCommonIcons'
import SearchSelect from '../../../../../components/re-useable/search-select-v3/SearchSelect'
import useProjectStore from '../../../project.store'

const { Text } = Typography

interface IProps {
  open: boolean
  project: ProjectEntity
  members: ProjectMemberEntity[]
  loading: boolean
  onCancel: () => void
  onFinish: () => void
}

const ModalMemberManagement = ({
  open,
  project,
  members,
  loading,
  onCancel,
  onFinish,
}: IProps) => {
  const { token } = useAuth()

  const [showCreate, setShowCreate] = useState<ProjectMemberEntity['role']>()
  const [confirmDeleteMemberId, setConfirmDeleteMemberId] = useState<string>()

  const { currentUser, isAdmin, isLeader } = useProjectStore()

  const isEditable = isAdmin || isLeader

  const userSelectData = useHandleSelectData({
    getListDataFunction: async (query) =>
      getListUserNotInProject(token, {
        ...query,
        projectId: project.id,
        filter: {
          departmentId: project.departmentId || undefined,
          ...query?.filter,
        },
      }),
    fetchOnFocus: true,
  })

  const { handleCreate, loading: loadingCreate } = useCreateProjectMember({
    customRequestHandler: true,
    onFail: () => {
      message.error('Hành động thất bại')
    },
    onFinish: () => {
      onFinish()
      userSelectData.handleResetAll()
      userSelectData.getInitData()
    },
  })
  const { handleDelete, loading: loadingDelete } = useDeleteProjectMember({
    customRequestHandler: true,
    onFail: () => {
      message.error('Hành động thất bại')
    },
    onFinish: () => {
      onFinish()
      userSelectData.handleResetAll()
      userSelectData.getInitData()
    },
  })

  const isLoading = loadingCreate || loadingDelete || loading

  const handleCancel = () => {
    if (isLoading) return
    userSelectData.handleResetAll()
    onCancel()
    setShowCreate(undefined)
    setConfirmDeleteMemberId(undefined)
  }

  const userOptionRender = (user: UserEntity) => (
    <Flex vertical>
      <Text strong style={{ color: 'inherit' }}>
        {user.fullname}
      </Text>
      <Text type="secondary" style={{ color: 'inherit' }}>
        {user.email}
      </Text>
    </Flex>
  )

  return (
    <Modal
      title="Thành viên trong dự án"
      open={open}
      onCancel={handleCancel}
      footer={null}
      closable={!isLoading}
      width={520}
    >
      <Spin spinning={isLoading} delay={200}>
        {(Object.keys(mapMemberRoleObj) as ProjectMemberEntity['role'][]).map(
          (role) => (
            <Fragment key={role}>
              <Divider plain orientation="left">
                {mapMemberRoleObj[role].name}
              </Divider>
              <Flex vertical gap={12}>
                <Row gutter={[8, 4]}>
                  {members
                    .filter((item) => item.role === role)
                    .map((member) => (
                      <Col xs={24} sm={8} key={member.id}>
                        <Popconfirm
                          title={`Xóa ${
                            mapMemberRole(member.role)?.name?.toLowerCase() ||
                            ''
                          }?`}
                          okText="Xóa"
                          cancelText="Hủy"
                          okButtonProps={{ danger: true, type: 'primary' }}
                          onConfirm={() => handleDelete(member.id)}
                          open={confirmDeleteMemberId === member.id}
                          onOpenChange={(isOpen) => {
                            if (!isOpen) setConfirmDeleteMemberId(undefined)
                          }}
                          placement="topRight"
                        >
                          <TagMember
                            user={member.user}
                            role={member.role}
                            onDelete={
                              (!isAdmin && currentUser?.id === member.userId) ||
                              !isEditable
                                ? undefined
                                : () => setConfirmDeleteMemberId(member.id)
                            }
                            tagProps={{ style: { width: '100%' } }}
                          />
                        </Popconfirm>
                      </Col>
                    ))}
                </Row>

                {isEditable ? (
                  <Flex justify="center">
                    {showCreate === role ? (
                      <SearchSelect
                        dataHandleResult={userSelectData}
                        labelRender={(rec) => rec.fullname}
                        optionRender={userOptionRender}
                        selectProps={{
                          value: null,
                          style: { width: '100%' },
                          defaultOpen: true,
                          suffixIcon: (
                            <Button
                              size="small"
                              type="text"
                              icon={<IconGlobalApp name="x" />}
                              onClick={() => setShowCreate(undefined)}
                            />
                          ),
                        }}
                        formItemProps={{ style: { margin: 0 } }}
                        onSelectGetItem={(item) => {
                          if (!item || !project.id) return
                          handleCreate({
                            projectId: project.id,
                            role,
                            userId: item.id,
                          })
                        }}
                      />
                    ) : (
                      <Button
                        type="link"
                        onClick={() => {
                          setShowCreate(role)
                          userSelectData.getInitData()
                        }}
                        icon={<IconGlobalApp name={mapCommonIcons.create} />}
                      >
                        Thêm {mapMemberRoleObj[role].name?.toLowerCase()}
                      </Button>
                    )}
                  </Flex>
                ) : null}
              </Flex>
            </Fragment>
          )
        )}
      </Spin>
    </Modal>
  )
}

export default ModalMemberManagement
